import {
    FloatingFocusManager,
    autoUpdate,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
    FloatingPortal,
} from '@floating-ui/react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { flip } from '@floating-ui/react-dom';
import classes from './Menu.module.css';
import clsx from 'clsx';
import { MenuContext } from './MenuContext';
import Icon from 'Components/Atoms/Icon/Icon';
import PropTypes from 'prop-types';

const Menu = forwardRef(function Menu(props, ref) {
    const {
        children,
        icon,
        iconColor = 'currentColor',
        iconSize = 24,
        hoverEffect = false,
        placement = 'bottom-end',
        className,
        zIndex = 'var(--z-index-dropdown)',
    } = props;
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
        open() {
            setIsOpen(true);
        },
        close() {
            setIsOpen(false);
        },
    }));

    const { x, y, refs, strategy, context } = useFloating({
        whileElementsMounted: autoUpdate,
        open: isOpen,
        placement,
        middleware: [
            flip({
                padding: 48,
            }),
        ],
        onOpenChange: setIsOpen,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'menu' }),
        useDismiss(context),
    ]);

    return (
        <MenuContext.Provider value={{ isOpen, setIsOpen }}>
            <div>
                <button
                    type="button"
                    ref={refs.setReference}
                    className={clsx(classes.menuButton, className, hoverEffect && classes.hoverEffect)}
                    {...getReferenceProps()}
                >
                    <Icon name={icon} color={hoverEffect ? 'currentColor' : iconColor} size={iconSize} />
                </button>
                {isOpen && (
                    <FloatingPortal>
                        <FloatingFocusManager context={context}>
                            <div
                                {...getFloatingProps({
                                    ref: refs.setFloating,
                                    style: {
                                        position: strategy,
                                        top: y ?? 0,
                                        left: x ?? 0,
                                        zIndex,
                                    },
                                })}
                            >
                                {children}
                            </div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </div>
        </MenuContext.Provider>
    );
});

export default Menu;

Menu.propTypes = {
    children: PropTypes.node.isRequired,
    icon: PropTypes.string.isRequired,
    iconColor: PropTypes.string,
    placement: PropTypes.string,
    iconSize: PropTypes.number,
    hoverEffect: PropTypes.bool,
    className: PropTypes.string,
    zIndex: PropTypes.string,
};
